import { ArrowLeft, ArrowRight } from '@mui/icons-material'
import { Button, Card, CardContent, Divider, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInDetailsDto } from 'dtos'
import { useDialogState, useLoadingState } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import AirbagTellTalePhotoDialog from './AirbagTellTalePhotoDialog'
import BackupCamPhotoDialog from './BackupCamPhotoDialog'
import CheckInClusterPhotoDialog from './CheckInClusterPhotoDialog'
import VehiclePhotoCard from '../VehiclePhotoCard'
import ClusterWithOdometerPhotoDialog from './ClusterWithOdometerPhotoDialog'
import ComboPhotoDialog from './ComboPhotoDialog'
import DriverAirbagPhotoDialog from './DriverAirbagPhotoDialog'
import FrontCornerPhotoDialog from './FrontCornerPhotoDialog'
import FrontHeadOnPhotoDialog from './FrontHeadOnPhotoDialog'
import ImporterDoorLabelPhotoDialog from './ImporterDoorLabelPhotoDialog'
import ManufacturersLabelPhotoDialog from './ManufacturersLabelPhotoDialog'
import PassengerAirbagPhotoDialog from './PassengerAirbagPhotoDialog'
import RearCornerPhotoDialog from './RearCornerPhotoDialog'
import RearHeadOnPhotoDialog from './RearHeadOnPhotoDialog'
import RoofPhotoDialog from './RoofPhotoDialog'
import TirePressureLabelPhotoDialog from './TirePressureLabelPhotoDialog'
import VehicleCheckInStepper from './VehicleCheckInStepper'
import VINOnDashboardDialog from './VINOnDashboardPhotoDialog'
import { CheckInPhotoDialogNavProps, shouldSubmitCheckInPhotoSave } from './checkInPhotoDialogCommon'
import { VehicleCheckInPhotosProps } from './index'

const CHECK_IN_PHOTO_STEP3_DIALOG_KEYS = [
  'manufacturersLabelPhotoDialogOpen',
  'tirePressureLabelPhotoDialogOpen',
  'importerDoorLabelPhotoDialogOpen',
  'comboPhotoDialogOpen',
  'checkInClusterPhotoDialogOpen',
  'clusterWithOdometerPhotoDialogOpen',
  'driverAirbagPhotoDialogOpen',
  'backupCamPhotoDialogOpen',
  'airbagTellTalePhotoDialogOpen',
  'passengerAirbagPhotoDialogOpen',
  'vinOnDashboardPhotoDialogOpen',
  'roofPhotoDialogOpen',
  'frontCornerPhotoDialogOpen',
  'frontHeadOnPhotoDialogOpen',
  'rearCornerPhotoDialogOpen',
  'rearHeadOnPhotoDialogOpen'
] as const

type CheckInPhotoStep3DialogKey = (typeof CHECK_IN_PHOTO_STEP3_DIALOG_KEYS)[number]

interface Step3CheckInPhotosProps extends VehicleCheckInPhotosProps {
  onBack: () => void
  onNext: () => void
  vehicleCheckInDetails: VehicleCheckInDetailsDto
  vehicleGuid?: string
}

export default function Step3CheckInPhotos({
  onBack,
  onNext,
  vehicleCheckInDetails,
  vehicleGuid,

  manufacturersLabelPhoto,
  setManufacturersLabelPhoto,
  tirePressureLabelPhoto,
  setTirePressureLabelPhoto,
  importerDoorLabelPhoto,
  setImporterDoorLabelPhoto,
  comboPhoto,
  setComboPhoto,
  checkInClusterPhoto,
  setCheckInClusterPhoto,
  clusterWithOdometerPhoto,
  setClusterWithOdometerPhoto,
  driverAirbagPhoto,
  setDriverAirbagPhoto,
  backupCamPhoto,
  setBackupCamPhoto,
  airbagTellTalePhoto,
  setAirbagTellTalePhoto,
  passengerAirbagPhoto,
  setPassengerAirbagPhoto,
  vinOnDashboardPhoto,
  setVinOnDashboardPhoto,
  roofPhoto,
  setRoofPhoto,
  frontCornerPhoto,
  setFrontCornerPhoto,
  frontHeadOnPhoto,
  setFrontHeadOnPhoto,
  rearCornerPhoto,
  setRearCornerPhoto,
  rearHeadOnPhoto,
  setRearHeadOnPhoto
}: Step3CheckInPhotosProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isUpdatingManufacturersLabel: false,
    isDeletingManufacturersLabel: false,
    isUpdatingTirePressureLabel: false,
    isDeletingTirePressureLabel: false,
    isUpdatingImporterDoorLabel: false,
    isDeletingImporterDoorLabel: false,
    isUpdatingComboPhoto: false,
    isDeletingComboPhoto: false,
    isUpdatingCheckInClusterPhoto: false,
    isDeletingCheckInClusterPhoto: false,
    isUpdatingClusterWithOdometerPhoto: false,
    isDeletingClusterWithOdometerPhoto: false,
    isUpdatingDriverAirbagPhoto: false,
    isDeletingDriverAirbagPhoto: false,
    isUpdatingBackupCamPhoto: false,
    isDeletingBackupCamPhoto: false,
    isUpdatingAirbagTellTalePhoto: false,
    isDeletingAirbagTellTalePhoto: false,
    isUpdatingPassengerAirbagPhoto: false,
    isDeletingPassengerAirbagPhoto: false,
    isUpdatingVINOnDashboardPhoto: false,
    isDeletingVINOnDashboardPhoto: false,
    isUpdatingRoofPhoto: false,
    isDeletingRoofPhoto: false,
    isUpdatingFrontCornerPhoto: false,
    isDeletingFrontCornerPhoto: false,
    isUpdatingFrontHeadOnPhoto: false,
    isDeletingFrontHeadOnPhoto: false,
    isUpdatingRearCornerPhoto: false,
    isDeletingRearCornerPhoto: false,
    isUpdatingRearHeadOnPhoto: false,
    isDeletingRearHeadOnPhoto: false
  })

  const [dialogState, setDialogState] = useDialogState({
    manufacturersLabelPhotoDialogOpen: false,
    tirePressureLabelPhotoDialogOpen: false,
    importerDoorLabelPhotoDialogOpen: false,
    comboPhotoDialogOpen: false,
    checkInClusterPhotoDialogOpen: false,
    clusterWithOdometerPhotoDialogOpen: false,
    driverAirbagPhotoDialogOpen: false,
    backupCamPhotoDialogOpen: false,
    airbagTellTalePhotoDialogOpen: false,
    passengerAirbagPhotoDialogOpen: false,
    vinOnDashboardPhotoDialogOpen: false,
    roofPhotoDialogOpen: false,
    frontCornerPhotoDialogOpen: false,
    frontHeadOnPhotoDialogOpen: false,
    rearCornerPhotoDialogOpen: false,
    rearHeadOnPhotoDialogOpen: false
  })

  const getPhotoDialogNav = (current: CheckInPhotoStep3DialogKey): CheckInPhotoDialogNavProps => {
    const idx = CHECK_IN_PHOTO_STEP3_DIALOG_KEYS.indexOf(current)
    return {
      isFirstInSequence: idx === 0,
      isLastInSequence: idx === CHECK_IN_PHOTO_STEP3_DIALOG_KEYS.length - 1,
      onGoToPrevious: () => {
        setDialogState(current, false)
        if (idx > 0) {
          setDialogState(CHECK_IN_PHOTO_STEP3_DIALOG_KEYS[idx - 1], true)
        }
      }
    }
  }

  const afterPhotoSaveNavigate = (current: CheckInPhotoStep3DialogKey, goToNext?: boolean) => {
    setDialogState(current, false)
    if (goToNext) {
      const idx = CHECK_IN_PHOTO_STEP3_DIALOG_KEYS.indexOf(current)
      if (idx >= 0 && idx < CHECK_IN_PHOTO_STEP3_DIALOG_KEYS.length - 1) {
        setDialogState(CHECK_IN_PHOTO_STEP3_DIALOG_KEYS[idx + 1], true)
      }
    }
  }

  const skipPhotoSaveWhenUnchanged = <T extends object>(
    current: CheckInPhotoStep3DialogKey,
    initialValues: T,
    values: T,
    meta?: { goToNext?: boolean },
    ignoredKeys: Array<keyof T> = []
  ) => {
    if (shouldSubmitCheckInPhotoSave(initialValues, values, ignoredKeys)) {
      return false
    }

    afterPhotoSaveNavigate(current, meta?.goToNext)
    return true
  }

  return (
    <>
      <ManufacturersLabelPhotoDialog
        initialValues={manufacturersLabelPhoto}
        isLoading={loadingState.isDeletingManufacturersLabel || loadingState.isUpdatingManufacturersLabel}
        nav={getPhotoDialogNav('manufacturersLabelPhotoDialogOpen')}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('manufacturersLabelPhotoDialogOpen', manufacturersLabelPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingManufacturersLabel', true)
          controllers
            .updateVehicleCheckInManufacturersLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar("Manufacturer's Label Saved Successfully!", { variant: 'success' })
              setManufacturersLabelPhoto(response.value)
              afterPhotoSaveNavigate('manufacturersLabelPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingManufacturersLabel', false)
            })
        }}
        onDelete={() => {
          setLoadingState('isDeletingManufacturersLabel', true)
          controllers
            .deleteVehicleCheckInManufacturersLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar("Manufacturer's Label Deleted Successfully!", { variant: 'success' })
              setManufacturersLabelPhoto(response.value)
              setDialogState('manufacturersLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingManufacturersLabel', false)
            })
        }}
        open={dialogState.manufacturersLabelPhotoDialogOpen}
        onClose={() => {
          setDialogState('manufacturersLabelPhotoDialogOpen', false)
        }}
        vehicleGuid={vehicleGuid!}
      />

      <TirePressureLabelPhotoDialog
        initialValues={tirePressureLabelPhoto}
        isLoading={loadingState.isDeletingTirePressureLabel || loadingState.isUpdatingTirePressureLabel}
        nav={getPhotoDialogNav('tirePressureLabelPhotoDialogOpen')}
        onClose={() => {
          setDialogState('tirePressureLabelPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingTirePressureLabel', true)
          controllers
            .deleteVehicleCheckInTirePressureLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Tire Pressure Label Photo Deleted Successfully!', { variant: 'success' })
              setTirePressureLabelPhoto(response.value)
              setDialogState('tirePressureLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingTirePressureLabel', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('tirePressureLabelPhotoDialogOpen', tirePressureLabelPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingTirePressureLabel', true)
          controllers
            .updateVehicleCheckInTirePressureLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Tire Pressure Label Saved Successfully!', { variant: 'success' })
              setTirePressureLabelPhoto(response.value)
              afterPhotoSaveNavigate('tirePressureLabelPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingTirePressureLabel', false)
            })
        }}
        open={dialogState.tirePressureLabelPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <ImporterDoorLabelPhotoDialog
        initialValues={importerDoorLabelPhoto}
        isLoading={loadingState.isDeletingImporterDoorLabel || loadingState.isUpdatingImporterDoorLabel}
        nav={getPhotoDialogNav('importerDoorLabelPhotoDialogOpen')}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('importerDoorLabelPhotoDialogOpen', importerDoorLabelPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingImporterDoorLabel', true)
          controllers
            .updateVehicleCheckInImporterDoorLabelPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Importer Door Label Saved Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              afterPhotoSaveNavigate('importerDoorLabelPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingImporterDoorLabel', false)
            })
        }}
        onDelete={() => {
          setLoadingState('isDeletingImporterDoorLabel', true)
          controllers
            .deleteVehicleCheckInImporterDoorLabelPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Importer Door Label Deleted Successfully!', { variant: 'success' })
              setImporterDoorLabelPhoto(response.value)
              setDialogState('importerDoorLabelPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingImporterDoorLabel', false)
            })
        }}
        open={dialogState.importerDoorLabelPhotoDialogOpen}
        onClose={() => {
          setDialogState('importerDoorLabelPhotoDialogOpen', false)
        }}
        vehicleGuid={vehicleGuid!}
      />

      <ComboPhotoDialog
        initialValues={comboPhoto}
        isLoading={loadingState.isDeletingComboPhoto || loadingState.isUpdatingComboPhoto}
        nav={getPhotoDialogNav('comboPhotoDialogOpen')}
        onClose={() => {
          setDialogState('comboPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingComboPhoto', true)
          controllers
            .deleteVehicleCheckInComboPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Combo Photo Deleted Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              setDialogState('comboPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingComboPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('comboPhotoDialogOpen', comboPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingComboPhoto', true)
          controllers
            .updateVehicleCheckInComboPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Combo Photo Saved Successfully!', { variant: 'success' })
              setComboPhoto(response.value)
              afterPhotoSaveNavigate('comboPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingComboPhoto', false)
            })
        }}
        open={dialogState.comboPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <CheckInClusterPhotoDialog
        initialValues={checkInClusterPhoto}
        isLoading={loadingState.isDeletingCheckInClusterPhoto || loadingState.isUpdatingCheckInClusterPhoto}
        nav={getPhotoDialogNav('checkInClusterPhotoDialogOpen')}
        onClose={() => {
          setDialogState('checkInClusterPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingCheckInClusterPhoto', true)
          controllers
            .deleteVehicleCheckInCheckInClusterPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Speedometer Before Modification Photo Deleted Successfully!', { variant: 'success' })
              setCheckInClusterPhoto(response.value)
              setDialogState('checkInClusterPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingCheckInClusterPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (
            skipPhotoSaveWhenUnchanged(
              'checkInClusterPhotoDialogOpen',
              checkInClusterPhoto,
              values,
              meta,
              ['alsoUseForOdometerBeforeModificationPhoto']
            )
          ) {
            return
          }
          setLoadingState('isUpdatingCheckInClusterPhoto', true)
          controllers
            .updateVehicleCheckInCheckInClusterPhotoInformation(vehicleGuid!, values)
            .then(response => {
              return controllers.getVehicleCheckInClusterWithOdometerPhotoInformation(vehicleGuid!).then(odometerResponse => {
                enqueueSnackbar('Speedometer Before Modification Saved Successfully!', { variant: 'success' })
                setCheckInClusterPhoto(response.value)
                setClusterWithOdometerPhoto(odometerResponse.value)
                afterPhotoSaveNavigate('checkInClusterPhotoDialogOpen', meta?.goToNext)
              })
            })
            .finally(() => {
              setLoadingState('isUpdatingCheckInClusterPhoto', false)
            })
        }}
        open={dialogState.checkInClusterPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <ClusterWithOdometerPhotoDialog
        initialValues={clusterWithOdometerPhoto}
        isLoading={loadingState.isDeletingClusterWithOdometerPhoto || loadingState.isUpdatingClusterWithOdometerPhoto}
        nav={getPhotoDialogNav('clusterWithOdometerPhotoDialogOpen')}
        onClose={() => {
          setDialogState('clusterWithOdometerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingClusterWithOdometerPhoto', true)
          controllers
            .deleteVehicleCheckInClusterWithOdometerPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Odometer Before Modification Photo Deleted Successfully!', { variant: 'success' })
              setClusterWithOdometerPhoto(response.value)
              setDialogState('clusterWithOdometerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingClusterWithOdometerPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('clusterWithOdometerPhotoDialogOpen', clusterWithOdometerPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingClusterWithOdometerPhoto', true)
          controllers
            .updateVehicleCheckInClusterWithOdometerPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Odometer Before Modification Saved Successfully!', { variant: 'success' })
              setClusterWithOdometerPhoto(response.value)
              afterPhotoSaveNavigate('clusterWithOdometerPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingClusterWithOdometerPhoto', false)
            })
        }}
        open={dialogState.clusterWithOdometerPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <DriverAirbagPhotoDialog
        initialValues={driverAirbagPhoto}
        isLoading={loadingState.isDeletingDriverAirbagPhoto || loadingState.isUpdatingDriverAirbagPhoto}
        nav={getPhotoDialogNav('driverAirbagPhotoDialogOpen')}
        onClose={() => {
          setDialogState('driverAirbagPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingDriverAirbagPhoto', true)
          controllers
            .deleteVehicleCheckInDriverAirbagPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Driver Airbag Photo Deleted Successfully!', { variant: 'success' })
              setDriverAirbagPhoto(response.value)
              setDialogState('driverAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingDriverAirbagPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('driverAirbagPhotoDialogOpen', driverAirbagPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingDriverAirbagPhoto', true)
          controllers
            .updateVehicleCheckInDriverAirbagPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Driver Airbag Saved Successfully!', { variant: 'success' })
              setDriverAirbagPhoto(response.value)
              afterPhotoSaveNavigate('driverAirbagPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingDriverAirbagPhoto', false)
            })
        }}
        open={dialogState.driverAirbagPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <BackupCamPhotoDialog
        initialValues={backupCamPhoto}
        isLoading={loadingState.isDeletingBackupCamPhoto || loadingState.isUpdatingBackupCamPhoto}
        nav={getPhotoDialogNav('backupCamPhotoDialogOpen')}
        onClose={() => {
          setDialogState('backupCamPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingBackupCamPhoto', true)
          controllers
            .deleteVehicleCheckInBackupCamPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Backup Cam Photo Deleted Successfully!', { variant: 'success' })
              setBackupCamPhoto(response.value)
              setDialogState('backupCamPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingBackupCamPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('backupCamPhotoDialogOpen', backupCamPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingBackupCamPhoto', true)
          controllers
            .updateVehicleCheckInBackupCamPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Backup Cam Saved Successfully!', { variant: 'success' })
              setBackupCamPhoto(response.value)
              afterPhotoSaveNavigate('backupCamPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingBackupCamPhoto', false)
            })
        }}
        open={dialogState.backupCamPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <AirbagTellTalePhotoDialog
        initialValues={airbagTellTalePhoto}
        isLoading={loadingState.isDeletingAirbagTellTalePhoto || loadingState.isUpdatingAirbagTellTalePhoto}
        nav={getPhotoDialogNav('airbagTellTalePhotoDialogOpen')}
        onClose={() => {
          setDialogState('airbagTellTalePhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingAirbagTellTalePhoto', true)
          controllers
            .deleteVehicleCheckInAirbagTellTalePhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Airbag Tell-Tale Photo Deleted Successfully!', { variant: 'success' })
              setAirbagTellTalePhoto(response.value)
              setDialogState('airbagTellTalePhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingAirbagTellTalePhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('airbagTellTalePhotoDialogOpen', airbagTellTalePhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingAirbagTellTalePhoto', true)
          controllers
            .updateVehicleCheckInAirbagTellTalePhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Airbag Tell-Tale Saved Successfully!', { variant: 'success' })
              setAirbagTellTalePhoto(response.value)
              afterPhotoSaveNavigate('airbagTellTalePhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingAirbagTellTalePhoto', false)
            })
        }}
        open={dialogState.airbagTellTalePhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <PassengerAirbagPhotoDialog
        initialValues={passengerAirbagPhoto}
        isLoading={loadingState.isDeletingPassengerAirbagPhoto || loadingState.isUpdatingPassengerAirbagPhoto}
        nav={getPhotoDialogNav('passengerAirbagPhotoDialogOpen')}
        onClose={() => {
          setDialogState('passengerAirbagPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingPassengerAirbagPhoto', true)
          controllers
            .deleteVehicleCheckInPassengerAirbagPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Passenger Airbag Photo Deleted Successfully!', { variant: 'success' })
              setPassengerAirbagPhoto(response.value)
              setDialogState('passengerAirbagPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingPassengerAirbagPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('passengerAirbagPhotoDialogOpen', passengerAirbagPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingPassengerAirbagPhoto', true)
          controllers
            .updateVehicleCheckInPassengerAirbagPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Passenger Airbag Saved Successfully!', { variant: 'success' })
              setPassengerAirbagPhoto(response.value)
              afterPhotoSaveNavigate('passengerAirbagPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingPassengerAirbagPhoto', false)
            })
        }}
        open={dialogState.passengerAirbagPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <VINOnDashboardDialog
        initialValues={vinOnDashboardPhoto}
        isLoading={loadingState.isDeletingVINOnDashboardPhoto || loadingState.isUpdatingVINOnDashboardPhoto}
        nav={getPhotoDialogNav('vinOnDashboardPhotoDialogOpen')}
        onClose={() => {
          setDialogState('vinOnDashboardPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingVINOnDashboardPhoto', true)
          controllers
            .deleteVehicleCheckInVINOnDashboardPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('VIN on Dashboard Photo Deleted Successfully!', { variant: 'success' })
              setVinOnDashboardPhoto(response.value)
              setDialogState('vinOnDashboardPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingVINOnDashboardPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('vinOnDashboardPhotoDialogOpen', vinOnDashboardPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingVINOnDashboardPhoto', true)
          controllers
            .updateVehicleCheckInVINOnDashboardPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('VIN on Dashboard Saved Successfully!', { variant: 'success' })
              setVinOnDashboardPhoto(response.value)
              afterPhotoSaveNavigate('vinOnDashboardPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingVINOnDashboardPhoto', false)
            })
        }}
        open={dialogState.vinOnDashboardPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <RoofPhotoDialog
        initialValues={roofPhoto}
        isLoading={loadingState.isDeletingRoofPhoto || loadingState.isUpdatingRoofPhoto}
        nav={getPhotoDialogNav('roofPhotoDialogOpen')}
        onClose={() => {
          setDialogState('roofPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRoofPhoto', true)
          controllers
            .deleteVehicleCheckInRoofPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Roof Photo Deleted Successfully!', { variant: 'success' })
              setRoofPhoto(response.value)
              setDialogState('roofPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRoofPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('roofPhotoDialogOpen', roofPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingRoofPhoto', true)
          controllers
            .updateVehicleCheckInRoofPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Roof Saved Successfully!', { variant: 'success' })
              setRoofPhoto(response.value)
              afterPhotoSaveNavigate('roofPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingRoofPhoto', false)
            })
        }}
        open={dialogState.roofPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <FrontCornerPhotoDialog
        initialValues={frontCornerPhoto}
        isLoading={loadingState.isDeletingFrontCornerPhoto || loadingState.isUpdatingFrontCornerPhoto}
        nav={getPhotoDialogNav('frontCornerPhotoDialogOpen')}
        onClose={() => {
          setDialogState('frontCornerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingFrontCornerPhoto', true)
          controllers
            .deleteVehicleCheckInFrontCornerPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Front Corner Photo Deleted Successfully!', { variant: 'success' })
              setFrontCornerPhoto(response.value)
              setDialogState('frontCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingFrontCornerPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('frontCornerPhotoDialogOpen', frontCornerPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingFrontCornerPhoto', true)
          controllers
            .updateVehicleCheckInFrontCornerPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Front Corner Saved Successfully!', { variant: 'success' })
              setFrontCornerPhoto(response.value)
              afterPhotoSaveNavigate('frontCornerPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingFrontCornerPhoto', false)
            })
        }}
        open={dialogState.frontCornerPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <FrontHeadOnPhotoDialog
        initialValues={frontHeadOnPhoto}
        isLoading={loadingState.isDeletingFrontHeadOnPhoto || loadingState.isUpdatingFrontHeadOnPhoto}
        nav={getPhotoDialogNav('frontHeadOnPhotoDialogOpen')}
        onClose={() => {
          setDialogState('frontHeadOnPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingFrontHeadOnPhoto', true)
          controllers
            .deleteVehicleCheckInFrontHeadOnPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Front Head On Photo Deleted Successfully!', { variant: 'success' })
              setFrontHeadOnPhoto(response.value)
              setDialogState('frontHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingFrontHeadOnPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('frontHeadOnPhotoDialogOpen', frontHeadOnPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingFrontHeadOnPhoto', true)
          controllers
            .updateVehicleCheckInFrontHeadOnPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Front Head On Saved Successfully!', { variant: 'success' })
              setFrontHeadOnPhoto(response.value)
              afterPhotoSaveNavigate('frontHeadOnPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingFrontHeadOnPhoto', false)
            })
        }}
        open={dialogState.frontHeadOnPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <RearCornerPhotoDialog
        initialValues={rearCornerPhoto}
        isLoading={loadingState.isDeletingRearCornerPhoto || loadingState.isUpdatingRearCornerPhoto}
        nav={getPhotoDialogNav('rearCornerPhotoDialogOpen')}
        onClose={() => {
          setDialogState('rearCornerPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRearCornerPhoto', true)
          controllers
            .deleteVehicleCheckInRearCornerPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Rear Corner Photo Deleted Successfully!', { variant: 'success' })
              setRearCornerPhoto(response.value)
              setDialogState('rearCornerPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRearCornerPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('rearCornerPhotoDialogOpen', rearCornerPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingRearCornerPhoto', true)
          controllers
            .updateVehicleCheckInRearCornerPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Rear Corner Saved Successfully!', { variant: 'success' })
              setRearCornerPhoto(response.value)
              afterPhotoSaveNavigate('rearCornerPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingRearCornerPhoto', false)
            })
        }}
        open={dialogState.rearCornerPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <RearHeadOnPhotoDialog
        initialValues={rearHeadOnPhoto}
        isLoading={loadingState.isDeletingRearHeadOnPhoto || loadingState.isUpdatingRearHeadOnPhoto}
        nav={getPhotoDialogNav('rearHeadOnPhotoDialogOpen')}
        onClose={() => {
          setDialogState('rearHeadOnPhotoDialogOpen', false)
        }}
        onDelete={() => {
          setLoadingState('isDeletingRearHeadOnPhoto', true)
          controllers
            .deleteVehicleCheckInRearHeadOnPhoto(vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Rear Head On Photo Deleted Successfully!', { variant: 'success' })
              setRearHeadOnPhoto(response.value)
              setDialogState('rearHeadOnPhotoDialogOpen', false)
            })
            .finally(() => {
              setLoadingState('isDeletingRearHeadOnPhoto', false)
            })
        }}
        onSave={(values, meta) => {
          if (skipPhotoSaveWhenUnchanged('rearHeadOnPhotoDialogOpen', rearHeadOnPhoto, values, meta)) {
            return
          }
          setLoadingState('isUpdatingRearHeadOnPhoto', true)
          controllers
            .updateVehicleCheckInRearHeadOnPhotoInformation(vehicleGuid!, values)
            .then(response => {
              enqueueSnackbar('Rear Head On Saved Successfully!', { variant: 'success' })
              setRearHeadOnPhoto(response.value)
              afterPhotoSaveNavigate('rearHeadOnPhotoDialogOpen', meta?.goToNext)
            })
            .finally(() => {
              setLoadingState('isUpdatingRearHeadOnPhoto', false)
            })
        }}
        open={dialogState.rearHeadOnPhotoDialogOpen}
        vehicleGuid={vehicleGuid!}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='center'>
            <Grid item xs={12}>
              <VehicleCheckInStepper activeStep={2} />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(manufacturersLabelPhoto.documentGuid)}
                documentGuid={manufacturersLabelPhoto.documentGuid}
                onClick={() => {
                  setDialogState('manufacturersLabelPhotoDialogOpen', true)
                }}
                type='manufacturers-label'
                vehicleGuid={vehicleGuid}
                warning={manufacturersLabelPhoto.vehicleCheckInIsManufacturersLabelPhotoMissing}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(tirePressureLabelPhoto.documentGuid)}
                documentGuid={tirePressureLabelPhoto.documentGuid}
                onClick={() => {
                  setDialogState('tirePressureLabelPhotoDialogOpen', true)
                }}
                type='tire-pressure-label'
                vehicleGuid={vehicleGuid}
                warning={tirePressureLabelPhoto.vehicleCheckInIsTirePressureLabelPhotoMissing}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(importerDoorLabelPhoto.documentGuid)}
                disabled={vehicleCheckInDetails.convertCluster}
                documentGuid={importerDoorLabelPhoto.documentGuid}
                onClick={() => {
                  setDialogState('importerDoorLabelPhotoDialogOpen', true)
                }}
                type='importer-door-label'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(comboPhoto.documentGuid)}
                disabled={vehicleCheckInDetails.convertCluster}
                documentGuid={comboPhoto.documentGuid}
                onClick={() => {
                  setDialogState('comboPhotoDialogOpen', true)
                }}
                type='combo'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(checkInClusterPhoto.documentGuid)}
                documentGuid={checkInClusterPhoto.documentGuid}
                onClick={() => {
                  setDialogState('checkInClusterPhotoDialogOpen', true)
                }}
                type='check-in-cluster'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(clusterWithOdometerPhoto.documentGuid)}
                documentGuid={clusterWithOdometerPhoto.documentGuid}
                onClick={() => {
                  setDialogState('clusterWithOdometerPhotoDialogOpen', true)
                }}
                type='cluster-with-odometer'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(driverAirbagPhoto.documentGuid)}
                documentGuid={driverAirbagPhoto.documentGuid}
                onClick={() => {
                  setDialogState('driverAirbagPhotoDialogOpen', true)
                }}
                type='driver-airbag'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(backupCamPhoto.documentGuid)}
                disabled={backupCamPhoto.vehicleCheckInHasBackupCam === false}
                documentGuid={backupCamPhoto.documentGuid}
                onClick={() => {
                  setDialogState('backupCamPhotoDialogOpen', true)
                }}
                type='backup-cam'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(airbagTellTalePhoto.documentGuid)}
                documentGuid={airbagTellTalePhoto.documentGuid}
                onClick={() => {
                  setDialogState('airbagTellTalePhotoDialogOpen', true)
                }}
                type='airbag-tell-tale'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(passengerAirbagPhoto.documentGuid)}
                disabled={passengerAirbagPhoto.vehicleCheckInHasPassengerAirbag === false}
                documentGuid={passengerAirbagPhoto.documentGuid}
                onClick={() => {
                  setDialogState('passengerAirbagPhotoDialogOpen', true)
                }}
                type='passenger-airbag'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(vinOnDashboardPhoto.documentGuid)}
                documentGuid={vinOnDashboardPhoto.documentGuid}
                onClick={() => {
                  setDialogState('vinOnDashboardPhotoDialogOpen', true)
                }}
                type='vin-on-dashboard'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(roofPhoto.documentGuid)}
                documentGuid={roofPhoto.documentGuid}
                onClick={() => {
                  setDialogState('roofPhotoDialogOpen', true)
                }}
                type='roof'
                vehicleGuid={vehicleGuid!}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(frontCornerPhoto.documentGuid)}
                documentGuid={frontCornerPhoto.documentGuid}
                onClick={() => {
                  setDialogState('frontCornerPhotoDialogOpen', true)
                }}
                type='front-corner'
                vehicleGuid={vehicleGuid!}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(frontHeadOnPhoto.documentGuid)}
                documentGuid={frontHeadOnPhoto.documentGuid}
                onClick={() => {
                  setDialogState('frontHeadOnPhotoDialogOpen', true)
                }}
                type='front-head-on'
                vehicleGuid={vehicleGuid!}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(rearCornerPhoto.documentGuid)}
                documentGuid={rearCornerPhoto.documentGuid}
                onClick={() => {
                  setDialogState('rearCornerPhotoDialogOpen', true)
                }}
                type='rear-corner'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <VehiclePhotoCard source='vehicle-check-in' lazyThumbnail showPlaceholderImage
                complete={Boolean(rearHeadOnPhoto.documentGuid)}
                documentGuid={rearHeadOnPhoto.documentGuid}
                onClick={() => {
                  setDialogState('rearHeadOnPhotoDialogOpen', true)
                }}
                type='rear-head-on'
                vehicleGuid={vehicleGuid}
              />
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || On the Vehicle Check-In – Check-In Photos screen, all 16 image thumbnails will always be visible and
                pressable, even if that image type does not apply to the vehicle being checked in. Pressing on a thumbnail opens
                the respective image details screen. || Each image cell will display its respective “placeholder” image until
                the user has uploaded an image for that image type, then a thumbnail of the user-uploaded image will be
                displayed. Manufacturer’s Label will show a green border if a photo was uploaded, a yellow border if the
                Manufacturer’s Label was marked as “Missing”, or a red border if a photo was not uploaded. || Tire Pressure
                Label will show a green border if a photo was uploaded, a yellow border if the Tire Pressure Label was marked as
                “Missing” or a red border if a photo was not uploaded. || Importer Door Label will show a green border if a
                photo was uploaded, a gray border if Convert Cluster = Yes was selected on the “Vehicle Details” step of vehicle
                check in, or a red border if a photo was not uploaded. || Combo will show a green border if a photo was
                uploaded, a gray border if Convert Cluster = Yes was selected on the “Vehicle Details” step of vehicle check in,
                or a red border if a photo was not uploaded. || Speedometer Before Modification, Odometer Before Modification, Driver Airbag, Airbag
                tell-tale, VIN on Dashboard, Roof, Front corner, Front head on, Rear corner, and Rear head on will show a green
                border if a photo was uploaded or a red border if a photo was not uploaded. || Backup Cam will show a green border if a photo
                was uploaded, a gray border if “Vehicle does not have Backup Cam” was selected during vehicle check in photo
                capture, or a red border if a photo was not uploaded. || Passenger Airbag will show a green border if a photo
                was uploaded, a gray border if “Vehicle does not have Passenger Airbag” was selected during vehicle check in
                photo capture, or a red border if a photo was not uploaded. ||
              </ExplanationAccordion>
            </Grid>

            <Grid item xs={12}>
              <Button color='primary' endIcon={<ArrowRight />} fullWidth onClick={onNext} type='submit' variant='contained'>
                NEXT
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button color='primary' startIcon={<ArrowLeft />} fullWidth onClick={onBack} variant='outlined'>
                PREVIOUS
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
